<template>
  <div>
    <!-- Section Separator -->
    <div class="section-wrapper">
      <hr class="section-separator">
    </div>

    <!-- Mes de inicio Section -->
    <div class="section-wrapper section-fade-in">
      <div class="mes-inicio-section">
        <div class="section-content">
          <!-- Title -->
          <h2 class="section-title mb-2">{{ config.sections.mesInicio.title }}</h2>
          
          <!-- Subtitle -->
          <p class="section-subtitle mb-4">
            {{ config.sections.mesInicio.subtitle }}
          </p>
          
          <!-- Loading State for Months -->
          <div v-if="loading" class="loading-months">
            <output class="spinner-border text-primary" aria-label="Cargando meses disponibles"></output>
            <p class="mt-2">Cargando meses disponibles para esta sede...</p>
          </div>
          
          <!-- Month Selection Buttons -->
          <div v-else class="month-buttons-container">
            <div class="month-buttons-row">
              <button 
                v-for="month in availableMonths" 
                :key="month.id"
                class="month-button"
                :class="{ 'month-button-selected': isMonthSelected(month), 'month-button-unselected': !isMonthSelected(month) }"
                @click="selectMonth(month)"
                type="button"
              >
                <div class="month-button-content">
                  <div class="month-name">{{ month.name }}</div>
                  <div class="month-subtitle">{{ getMonthStartDate(month) }}</div>
                </div>
              </button>
            </div>
            
            <!-- No months available message -->
            <div v-if="availableMonths.length === 0" class="no-months-message">
              <p class="text-muted">No hay meses disponibles para esta sede en este momento.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { coursePeriodsService, programsService } from '@/services'

export default {
  name: 'MonthSelector',
  props: {
    programa: {
      type: String,
      default: 'Presencial'
    },
    year: {
      type: String,
      default: '4'
    },
    modalidad: {
      type: String,
      default: null
    },
    sedeId: {
      type: [String, Number],
      default: null
    },
    programId: {
      type: [String, Number],
      default: null
    },
    selectedMonth: {
      type: [String, Number, Object],
      default: null
    }
  },
  data() {
    return {
      // === DATOS DINÁMICOS ===
      availableMonths: [],
      
      // === ESTADOS DE CARGA PARA MESES ===
      loading: false,
      
      // === CONFIGURACIÓN ESTÁTICA ===
      config: {
        sections: {
          mesInicio: {
            title: "Mes de inicio",
            subtitle: "Selecciona el mes de inicio de tu programa"
          }
        }
      }
    }
  },
  watch: {
    sedeId: {
      handler() {

        this.loadMonthsForSede()
      },
      immediate: true
    }
  },
  methods: {
    isMonthSelected(month) {
      if (this.selectedMonth == null || !month) return false
      if (typeof this.selectedMonth === 'object' && this.selectedMonth.id != null) {
        return String(this.selectedMonth.id) === String(month.id)
      }
      return String(this.selectedMonth) === String(month.id)
    },
    /**
     * Parsea YYYY-MM-DD sin usar Date (evita desfase UTC → mes incorrecto en la card).
     */
    parseIsoDateParts(iso) {
      if (!iso || typeof iso !== 'string') return null
      const m = iso.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
      if (!m) return null
      return {
        year: parseInt(m[1], 10),
        month1to12: parseInt(m[2], 10),
        day: parseInt(m[3], 10)
      }
    },

    /**
     * Texto para la card a partir del startDate de la API (ej. 2026-03-01 → 1 de marzo).
     */
    formatStartDateLabelFromApi(iso) {
      const parts = this.parseIsoDateParts(iso)
      if (!parts || parts.month1to12 < 1 || parts.month1to12 > 12) return null
      const monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ]
      const name = monthNames[parts.month1to12 - 1]
      return `${parts.day} de ${name}`
    },

    mapCoursePeriodsToMonths(raw) {
      const input = Array.isArray(raw) ? raw : []
      const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
      ]

      return input
        .map((p, idx) => {
          const iso = p?.startDate || p?.startAt || p?.date || null
          const fromIso = typeof iso === 'string' ? this.parseIsoDateParts(iso) : null

          // API course-periods: month 1–12, monthName, year, startDate "YYYY-MM-DD"
          const month1to12 =
            (typeof p?.month === 'number' ? p.month : null) ??
            (typeof p?.monthNumber === 'number' ? p.monthNumber : null) ??
            (fromIso ? fromIso.month1to12 : null) ??
            null

          let monthIndex =
            typeof p?.monthIndex === 'number'
              ? p.monthIndex
              : month1to12 != null
                ? month1to12 - 1
                : null

          if (monthIndex == null || monthIndex < 0 || monthIndex > 11) return null

          const year =
            p?.year ??
            (fromIso ? fromIso.year : null) ??
            new Date().getFullYear()

          const monthName = monthNames[monthIndex]
          const startDay =
            p?.startDay ??
            (fromIso ? fromIso.day : null)

          const startDateLabel =
            p?.startDateText ||
            this.formatStartDateLabelFromApi(iso) ||
            (startDay ? `${startDay} de ${monthName.toLowerCase()}` : null)

          // Nunca usar solo p.id numérico como id de la card: el padre interpreta números 1–12 como mes calendario.
          const stableId = `${monthName.toLowerCase()}-${year}-${p?.id != null ? `p${p.id}` : idx}`

          return {
            periodId: p?.id ?? null,
            id: stableId,
            name: p?.monthName || p?.name || `${monthName} ${year}`,
            subtitle: p?.subtitle || (startDateLabel ? `Inicio el ${startDateLabel}` : `Inicio en ${monthName.toLowerCase()}`),
            monthIndex,
            year,
            monthNumber: monthIndex + 1,
            startDay,
            startDateRaw: typeof iso === 'string' ? iso : null,
            startDateLabel,
            categoryId: p?.categoryId ?? null
          }
        })
        .filter(Boolean)
        // ordenar por año/mes para UI consistente
        .sort((a, b) => (Number(a.year) - Number(b.year)) || (a.monthIndex - b.monthIndex))
    },
    /**
     * true si la fecha de inicio del curso (día calendario) es anterior a hoy.
     * Prioriza startDate ISO de la API; si no hay, aproxima con año/mes del período.
     */
    isCourseStartBeforeToday(month) {
      if (!month) return false
      const iso = month.startDateRaw
      if (iso && typeof iso === 'string') {
        const parts = this.parseIsoDateParts(iso)
        if (parts && parts.year && parts.month1to12 >= 1 && parts.month1to12 <= 12 && parts.day >= 1) {
          const start = new Date(parts.year, parts.month1to12 - 1, parts.day)
          const now = new Date()
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
          return start < today
        }
      }
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()
      return (
        month.year < currentYear ||
        (month.year === currentYear && month.monthIndex < currentMonth)
      )
    },

    getMonthStartDate(month) {
      const verb = this.isCourseStartBeforeToday(month) ? 'Iniciadas' : 'Inicio'
      
      // Texto derivado del startDate ISO de course-periods (sin desfase de zona)
      if (month.startDateLabel) {
        return `${verb} el ${month.startDateLabel}`;
      }
      // Fallback programsService: startDate ya viene como "15 de abril" (texto)
      if (month.startDate && typeof month.startDate === 'string') {
        return `${verb} el ${month.startDate}`;
      }
      
      // Si tiene startDay, construir la fecha
      if (month.startDay != null) {
        const monthNames = [
          'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
          'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
        ];
        const monthName = monthNames[month.monthIndex];
        return `${verb} el ${month.startDay} de ${monthName}`;
      }
      
      // Si no hay fecha específica, solo mostrar el mes
      const monthName = month.name.split(' ')[0].toLowerCase();
      return month.subtitle || `${verb} en ${monthName}`;
    },
    
    async loadMonthsForSede() {
      this.loading = true; // Iniciar estado de carga
      try {
        if (!this.sedeId) {
          this.availableMonths = []
          return
        }

        // Nuevo endpoint: GET /api/course-periods (baseURL ya trae /api)
        // Mandamos parámetros “best effort” (si backend ignora alguno, no afecta).
        const raw = await coursePeriodsService.getCoursePeriods({
          locationId: this.sedeId,
          programId: this.programId,
          year: this.year,
          modalidad: this.modalidad || this.programa
        })

        const mapped = this.mapCoursePeriodsToMonths(raw)
        if (mapped.length > 0) {
          this.availableMonths = mapped
          return
        }

        // Fallback 1: resolver por el PROGRAMA seleccionado.
        // Cada programa tiene su propia meta con su propio meses_enable, así que
        // esta vía distingue programas que comparten modalidad (ej. Anual vs Invierno,
        // ambos "Presencial" pero con meses de inicio distintos).
        if (this.programId) {
          const byProgram = await programsService.getAvailableMonthsByProgram(this.programId)
          if (byProgram.length > 0) {
            this.availableMonths = byProgram
            return
          }
        }

        // Fallback 2: lógica anterior basada en la meta de la modalidad.
        // Menos precisa: no distingue programas que comparten modalidad.
        const modalidadToUse = this.modalidad || this.programa
        this.availableMonths = await programsService.getAvailableMonthsBySede(
          modalidadToUse,
          this.year,
          this.sedeId
        )
        
      } catch (error) {
        console.error('Error cargando meses:', error);
        // Fallback final
        try {
          // Priorizar el programa seleccionado también acá, por el mismo motivo
          // que en el camino principal: la modalidad no distingue Anual de Invierno.
          if (this.programId) {
            const byProgram = await programsService.getAvailableMonthsByProgram(this.programId)
            if (byProgram.length > 0) {
              this.availableMonths = byProgram
              return
            }
          }

          const modalidadToUse = this.modalidad || this.programa
          if (this.sedeId) {
            this.availableMonths = await programsService.getAvailableMonthsBySede(
              modalidadToUse,
              this.year,
              this.sedeId
            )
          } else {
            this.availableMonths = await programsService.getAvailableMonths(modalidadToUse, this.year)
          }
        } catch (e) {
          this.availableMonths = []
        }
      } finally {
        this.loading = false; // Finalizar estado de carga
      }
    },
    
    selectMonth(month) {
      if (!month || month.monthNumber == null) {
        console.error('Mes inválido:', month);
        return;
      }
      // Incluir monthNumber para el API de cursos; el id sirve para UI/stepper.
      this.$emit('month-selected', {
        id: month.id,
        monthNumber: month.monthNumber,
        year: month.year,
        periodId: month.periodId ?? null
      });
    }
  }
}
</script>

<style scoped>
/* Section Styles */
.section-wrapper {
  margin-bottom: 24px;
}

.section-separator {
  border: none;
  height: 1px;
  background-color: #e0e0e0;
  margin: 24px 0;
}

.section-fade-in {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.mes-inicio-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.section-content {
  width: 100%;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.4;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.loading-months {
  text-align: center;
  padding: 40px 0;
}

.month-buttons-container {
  width: 100%;
}

.month-buttons-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: flex-start;
}

.month-button {
  min-width: 160px;
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background-color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.month-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border-color: #3691D8;
}

.month-button-selected {
  background-color: #3691D8;
  color: white;
  border-color: #3691D8;
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
}

.month-button-unselected {
  background-color: white;
  color: #333;
}

.month-button-content {
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 8px;
}

.month-name {
  font-family: "Poppins-Medium";
  font-size: 1.2rem;
  margin: 0;
  line-height: 1.3;
}

.month-subtitle {
  font-family: "Poppins-Medium";
  font-size: 0.85rem;
  margin: 0;
  line-height: 1.3;
}

.month-button-selected .month-subtitle {
  color: rgba(255, 255, 255, 0.9);
}

.month-button-unselected .month-subtitle {
  color: #000000;
}

.no-months-message {
  text-align: center;
  padding: 40px 0;
}

/* Responsive styles */
@media (max-width: 768px) {
  .month-buttons-row {
    gap: 10px;
  }
  
  .month-button {
    min-width: 150px;
    padding: 16px 12px;
  }
  
  .month-name {
    font-size: 1.1rem;
  }
  
  .month-subtitle {
    font-size: 0.8rem;
  }
}
</style> 